module.exports = function buildConnectClaudeToYourBroker({ agentProofPoints }) {
  const SOURCE_VERIFIED_DATE = '2026-08-14';

  return {
    path: '/guides/connect-claude-to-your-broker',
    title: 'Connect Claude to Your Broker with BotSpot | Guide',
    description:
      'Connect Claude to BotSpot through OAuth, use supported broker context, understand trade approvals, and keep brokerage credentials out of AI prompts.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'Claude and broker connection guide',
      heading: 'Connect Claude to your broker through BotSpot',
      summary:
        'Claude connects to BotSpot through a remote OAuth MCP connector. Your brokerage account connects separately inside BotSpot. This keeps setup, broker permissions, and high-risk action approvals in their proper boundaries.',
      primaryAction: { label: 'Open BotSpot MCP setup', url: '/agents' },
      secondaryAction: {
        label: 'Connect a supported broker',
        url: '/account-settings/broker-connections?action=add',
      },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: '1. Understand what connects to what',
          paragraphs: [
            'The connection path is Claude → BotSpot MCP → your BotSpot account → a saved supported broker connection. Claude does not connect directly to the broker, and adding the Claude connector does not create or authorize a brokerage account.',
            'BotSpot supplies research, strategy, backtest, account-context, approval, and deployment workflows. The connected broker remains the account and execution layer. Broker products, permissions, market data, regions, fees, and order eligibility still apply.',
          ],
        },
        {
          heading: '2. Check access before setup',
          paragraphs: [
            'You need a BotSpot account and access to custom connectors in Claude. Anthropic currently supports remote custom connectors across Claude plans, but organization controls differ.',
          ],
          bullets: [
            'Individual users add a connector from Customize → Connectors.',
            'On Team and Enterprise plans, an Owner or Primary Owner must add the custom connector to the organization before members connect their own accounts.',
            'Connect the intended brokerage account inside BotSpot. Broker setup does not happen in Claude.',
            'Use a paper connection first when the selected broker and BotSpot connection support paper mode.',
          ],
        },
        {
          heading: '3. Add BotSpot as a custom Claude connector',
          paragraphs: [
            'In Claude, open Customize → Connectors, select Add custom connector, name it BotSpot, and enter https://mcp.botspot.trade. This is BotSpot’s Claude-specific remote MCP host.',
            'Complete the BotSpot OAuth login and consent flow. Then enable BotSpot for the conversation from the plus menu under Connectors.',
          ],
          bullets: [
            'Use the exact HTTPS host: https://mcp.botspot.trade.',
            'Authenticate through the browser OAuth flow.',
            'Do not paste a BotSpot API key, broker password, API secret, or private key into Claude.',
            'Enable only the connector tools needed for the current conversation.',
          ],
        },
        {
          heading: '4. Connect the broker inside BotSpot',
          paragraphs: [
            'Open BotSpot broker connections and add the account you intend to use. Authentication methods and available paper or live modes vary by broker.',
            'A supported broker connection does not mean every account can use every asset, order type, option level, margin feature, or trading session. Confirm account-level permissions with the broker.',
          ],
          bullets: [
            'Stocks and options: Alpaca supports paper and live modes; Charles Schwab is live-only; Tradier supports paper and live modes, with mode availability depending on authentication method.',
            'Futures: Tradovate and TopstepX support paper and live modes.',
            'Crypto: Alpaca supports paper and live modes; Kraken, Coinbase, WEEX, and Bitunix are currently live-only.',
            'WEEX terms restrict United States and Canada eligibility. BotSpot advises using Bitunix API credentials with withdrawals disabled.',
          ],
        },
        {
          heading: '5. Verify context with a read-first conversation',
          paragraphs: [
            'Enable BotSpot in a new Claude conversation and begin with a bounded read request. Confirm that Claude can identify the correct saved account, mode, strategy, or backtest before requesting any action.',
          ],
          bullets: [
            'Ask Claude to list available BotSpot broker connections and their paper or live modes.',
            'Name the exact saved broker account when asking for balances, positions, or orders.',
            'Ask Claude to summarize a selected strategy revision or backtest before changing or deploying it.',
            'Check timestamps and account labels before relying on returned account context.',
          ],
        },
        {
          heading: '6. Keep Claude approval and BotSpot authorization separate',
          paragraphs: [
            'Claude may ask whether a connector tool can run. That permission allows Claude to invoke the BotSpot tool; it is not final authorization for a brokerage action.',
            'BotSpot routes supported high-risk actions through a separate authorization flow. For a one-time trade, inspect the selected broker connection, account, paper or live mode, asset, side, quantity, order type, prices, and expiration before approval. Fresh login and live-risk acceptance may also be required.',
            'Starting, updating, or stopping an automated strategy is a separate deployment decision. Stopping a bot does not necessarily close positions already held at the broker.',
          ],
          bullets: [
            'Keep write-capable trading tools on Ask or Needs approval.',
            'Do not choose Allow always for a financial-action tool unless you fully understand and accept unsupervised use.',
            'Treat paper and live approvals as different decisions.',
            'Reject expired, ambiguous, unexpected, or wrong-account requests.',
          ],
        },
        {
          heading: '7. Reduce connector and credential risk',
          paragraphs: [
            'Anthropic warns that custom connectors can read or modify external data according to granted permissions. Connect only to the official BotSpot host, review requested access, and inspect tool inputs and outputs.',
            'Anthropic also warns that Research can invoke connector tools automatically. Disable write-capable BotSpot tools before using Research with the connector.',
          ],
          bullets: [
            'Never place broker credentials, private keys, recovery codes, or secrets in prompts.',
            'Prefer broker API credentials with the minimum required permissions and withdrawals disabled where supported.',
            'Disable irrelevant connector tools for each conversation.',
            'Watch for prompt injection in webpages, documents, and other untrusted content.',
            'Disconnect the connector in Claude to revoke Claude access; separately revoke BotSpot or broker credentials when those credentials may be compromised.',
          ],
        },
        {
          heading: '8. Troubleshoot without weakening controls',
          bullets: [
            'Connector missing: confirm BotSpot is enabled for the current conversation and, on managed plans, that an organization owner added it.',
            'OAuth fails: verify the exact host, finish browser login and consent, then reconnect.',
            'Tools look stale after a BotSpot update: remove and re-add the connector so Claude reloads its tool manifest.',
            'No broker account appears: connect or reconnect it inside BotSpot, not Claude.',
            'An order is rejected: check broker account permissions, product eligibility, market state, buying power, and order support before changing connector permissions.',
          ],
        },
        {
          heading: 'Limitations and risk',
          paragraphs: [
            'Claude can help inspect information and operate supported BotSpot workflows, but it cannot guarantee correct analysis, safe code, successful execution, or profitable results. AI output, backtests, paper fills, and broker data can be incomplete, delayed, or wrong.',
            'Review every material decision. Live trading remains exposed to market movement, latency, slippage, partial fills, fees, liquidity, outages, software defects, and broker restrictions.',
          ],
        },
        {
          heading: 'Practice this workflow live',
          paragraphs: [
            'Want help turning your Claude and broker connection into a repeatable research and trading workflow? Learn with Rob Grzesik in the live AI Trading Bootcamp. Review the course syllabus and decide whether the live format fits your goals.',
          ],
          links: [
            {
              label: 'Explore the AI Trading Course & Live Bootcamp',
              url: '/courses/ai-trading-bootcamp',
            },
          ],
        },
      ],
      faq: [
        {
          question: 'Does Claude connect directly to my broker?',
          answer:
            'No. Claude connects to BotSpot through remote MCP. Your brokerage account connects separately to BotSpot through a supported broker connection.',
        },
        {
          question: 'Do I need a BotSpot API key for Claude?',
          answer:
            'Not for the Claude app or Claude web custom connector. That setup uses BotSpot OAuth. Claude Code is a separate client with a separate documented setup path.',
        },
        {
          question: 'Which brokers can Claude use through BotSpot?',
          answer:
            'Current BotSpot connections include Alpaca, Charles Schwab, Tradier, Tradovate, Kraken, Coinbase, WEEX, Bitunix, and TopstepX. Assets, paper or live modes, authentication, regions, and account permissions vary. Check the current broker manifest before acting.',
        },
        {
          question: 'Can Claude place a broker trade without my review?',
          answer:
            'When direct trading is enabled, Claude can ask BotSpot to prepare a supported trade request. BotSpot requires a separate explicit authorization for the one-time high-risk action. Automated strategy deployment uses its own approval and operating workflow.',
        },
        {
          question: 'Should I give Claude my broker API key?',
          answer:
            'No. Configure broker credentials inside BotSpot’s protected broker-connection workflow. Do not paste broker secrets or private keys into Claude.',
        },
        {
          question: 'Why can Claude see BotSpot but not my brokerage account?',
          answer:
            'The Claude connector and broker connection are separate. Confirm the broker is saved and healthy in BotSpot, then name the intended saved account in the request.',
        },
      ],
      relatedLinks: [
        { label: 'BotSpot MCP for Agents', url: '/agents' },
        { label: 'View supported brokers', url: '/brokers' },
        {
          label: 'Connect a broker in BotSpot',
          url: '/account-settings/broker-connections?action=add',
        },
        {
          label: 'Compare paper and live trading',
          url: '/guides/paper-trading-vs-live-trading',
        },
        {
          label: 'Build an AI trading bot',
          url: '/guides/how-to-build-an-ai-trading-bot',
        },
      ],
      sources: [
        { label: 'BotSpot MCP for Agents', url: '/agents' },
        {
          label: 'BotSpot broker connection manifest',
          url: '/.well-known/botspot-manifest.json',
        },
        { label: 'BotSpot Terms of Service', url: '/terms' },
        {
          label: 'Anthropic custom remote connector guide',
          url: 'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp',
        },
        {
          label: 'Anthropic connector guide',
          url: 'https://support.claude.com/en/articles/11176164-use-connectors-to-extend-claude-s-capabilities',
        },
        {
          label: 'Anthropic tool-access guide',
          url: 'https://support.claude.com/en/articles/13730515-manage-claude-s-tool-access',
        },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
};
